<template>
  <b-sidebar
    id="sidebar-create-confirmed-scientific-topic"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ $t('Create') }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />
      </div>

      <validation-observer ref="topicForm">
        <b-form
          class="p-2"
          @submit.prevent
        >
          <b-form-group
            :label="$t('Dissertation topic (uz)')"
            label-for="topic-name-uz"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dissertation topic (uz)')"
              rules="required"
            >
              <b-form-input
                id="topic-name-uz"
                v-model="topic.name_uz"
                trim
              />
              <b-button
                variant="outline-primary"
                size="sm"
                class="mt-50"
                :disabled="!topic.name_uz"
                @click="openApprovedTopicsSearch('uz')"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('Search topic (uz)') }}
              </b-button>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Dissertation topic (ru)')"
            label-for="topic-name-ru"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dissertation topic (ru)')"
              rules="required"
            >
              <b-form-input
                id="topic-name-ru"
                v-model="topic.name_ru"
                trim
              />
              <b-button
                variant="outline-primary"
                size="sm"
                class="mt-50"
                :disabled="!topic.name_ru"
                @click="openApprovedTopicsSearch('ru')"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('Search topic (ru)') }}
              </b-button>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Dissertation topic (en)')"
            label-for="topic-name-en"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dissertation topic (en)')"
              rules="required"
            >
              <b-form-input
                id="topic-name-en"
                v-model="topic.name_en"
                trim
              />
              <b-button
                variant="outline-primary"
                size="sm"
                class="mt-50"
                :disabled="!topic.name_en"
                @click="openApprovedTopicsSearch('en')"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('Search topic (en)') }}
              </b-button>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Student')"
            label-for="topic-apply-doc"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Student')"
              rules="required"
            >
              <div
                v-if="topic.apply_doc"
                id="topic-apply-doc"
                class="input-group"
              >
                <div class="input-group-prepend">
                  <span class="input-group-text border-success">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                    />
                  </span>
                </div>
                <div
                  class="form-control border-success text-truncate"
                  :title="applyDocName"
                >
                  {{ applyDocName }}
                </div>
                <div class="input-group-append">
                  <span class="input-group-text">
                    <feather-icon
                      icon="XIcon"
                      class="cursor-pointer danger"
                      size="16"
                      @click="clearApplyDoc"
                    />
                  </span>
                </div>
              </div>
              <div
                v-else
                id="topic-apply-doc"
                v-b-modal.confirmed-scientific-topic-students-modal
                class="input-group cursor-pointer"
              >
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                    />
                  </span>
                </div>
                <div class="form-control text-muted cursor-pointer text-truncate">
                  {{ $t('Not selected') }}
                </div>
                <div class="input-group-append">
                  <span class="input-group-text cursor-pointer">
                    <feather-icon
                      icon="SearchIcon"
                      size="16"
                    />
                  </span>
                </div>
              </div>
              <b-form-input
                v-model="topic.apply_doc"
                class="d-none"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Protocol')"
            label-for="topic-protocol"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Protocol')"
              rules="required"
            >
              <v-select
                id="topic-protocol"
                v-model="topic.scientific_topic_protocol"
                :reduce="(option) => option.id"
                :options="protocols"
                :searchable="true"
                label="protocol_number"
                :placeholder="$t('Search')"
                @search="searchProtocols"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="isSubmitting"
              @click="submit(hide)"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-50"
              />
              {{ $t('Create') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              @click="clear(hide)"
            >
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>

      <b-modal
        id="confirmed-scientific-topic-students-modal"
        size="xl"
        :title="$t('Searchers List')"
      >
        <l-table
          ref="orgStudentsTable"
          :module-name="ORG_STUDENTS_MODULE_NAME"
          :table-columns="orgStudentColumns"
          :filter-class="filterClass"
          :table-config-options="{ endpoint: `${MODULE_NAME}/orgStudents`, syncRoute: false, perPage: 5 }"
          :table-props="orgStudentsTableProps"
          :table-listeners="orgStudentsTableListeners"
        >
          <template #cell(user)="{ data }">
            {{ data.value.full_name }}
          </template>

          <template #cell(dc_type)="{ data }">
            {{ $langName(getDcType(data.value).name) }}
          </template>

          <template #cell(direction)="{ data }">
            {{ data.value ? `${data.value.code} - ${$langName(data.value.name)}` : '-' }}
          </template>

          <template #cell(status)="{ data }">
            <b-badge :variant="getApplyDocumentStatusColor(data.value)">
              {{ $langName(getApplyDocumentStatus(data.value).name) }}
            </b-badge>
          </template>
        </l-table>

        <template #modal-footer="{ cancel }">
          <b-button
            variant="outline-secondary"
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </b-button>
        </template>
      </b-modal>

      <b-modal
        id="confirmed-scientific-topic-approved-topics-modal"
        size="xl"
        :title="$t('Approved scientific topics')"
        @shown="refreshApprovedTopicsSearch"
      >
        <l-table
          ref="approvedTopicsSearchTable"
          :module-name="APPROVED_TOPICS_MODULE_NAME"
          :table-columns="approvedTopicsColumns"
          search-placeholder="Search by scientific topic or FIO"
          :show-search="false"
          :show-table-top="false"
          :table-config-options="approvedTopicsTableConfigOptions"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              {{ $t('Checking') }} ...
            </div>
          </template>

          <template #cell(index)="{ data }">
            {{ data.index + 1 }}
          </template>

          <template #cell(fio)="{ data }">
            {{ valueOrDash(data.item && data.item.fio) }}
          </template>

          <template #cell(scientific_topic_name)="{ data }">
            {{ valueOrDash(data.item && data.item.scientific_topic_name) }}
          </template>

          <template #cell(scientific_topic_name_ru)="{ data }">
            {{ valueOrDash(data.item && data.item.scientific_topic_name_ru) }}
          </template>

          <template #cell(scientific_topic_name_en)="{ data }">
            {{ valueOrDash(data.item && data.item.scientific_topic_name_en) }}
          </template>
        </l-table>

        <template #modal-footer="{ cancel }">
          <b-button
            variant="outline-secondary"
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </b-button>
        </template>
      </b-modal>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BButton, BModal, VBModal, BBadge, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import { required } from '@/libs/validations/validations'
import store from '@/store'
import confirmedScientificTopicModule from '@/store/confirmed-scientific-topic/index'
import confirmedScientificTopicProtocolModule from '@/store/confirmed-scientific-topic-protocol/index'
import LTable from '@/views/components/LTable.vue'
import StudentList from '@/views/components/filters/service/StudentList'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BModal,
    BBadge,
    BSpinner,
    vSelect,
    ValidationObserver,
    ValidationProvider,
    LTable,
  },
  directives: {
    Ripple,
    'b-modal': VBModal,
  },
  data() {
    return {
      required,
      isSubmitting: false,
      approvedTopicsSearchValue: '',
      approvedTopicsSearchLang: 'uz',
      approvedTopicsColumns: [
        { key: 'index', label: '#', thStyle: { width: '80px' } },
        { key: 'fio', label: 'FIO' },
        { key: 'scientific_topic_name', label: 'Dissertation topic (uz)' },
        { key: 'scientific_topic_name_ru', label: 'Dissertation topic (ru)' },
        { key: 'scientific_topic_name_en', label: 'Dissertation topic (en)' },
      ],
      selectedApplyDocName: '',
      filterClass: new StudentList(),
      orgStudentColumns: [
        { key: 'user', label: 'Student', sortable: true },
        { key: 'dc_type', label: 'Dc Type', sortable: true },
        { key: 'direction', label: 'Direction', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
      ],
      orgStudentsTableProps: {
        tbodyTrClass: 'cursor-pointer',
      },
      orgStudentsTableListeners: {
        'row-clicked': this.selectApplyDoc,
      },
    }
  },
  computed: {
    topic: {
      get() {
        return this.$store.state.confirmedScientificTopic.topic
      },
      set(data) {
        this.$store.commit('confirmedScientificTopic/UPDATE', data)
      },
    },
    protocols() {
      return this.$store.state.confirmedScientificTopicProtocol?.protocols || []
    },
    applyDocName() {
      return this.selectedApplyDocName || this.topic.apply_doc_name
    },
    approvedTopicsTableConfigOptions() {
      return {
        endpoint: `${this.MODULE_NAME}/searchApprovedTopics`,
        syncRoute: false,
        perPage: 5,
        searchQuery: this.approvedTopicsSearchValue,
      }
    },
  },
  created() {
    localize(this.$i18n.locale)
    this.loadData()
  },
  methods: {
    loadData() {
      this.ensureProtocolModule()
      this.$store.dispatch('staticModule/dcType')
      this.$store.dispatch('staticModule/applyDocumentStatuses')
      this.$store.dispatch('confirmedScientificTopicProtocol/list', { limit: 20, ordering: '-id' })
    },
    searchProtocols(search, loading) {
      this.ensureProtocolModule()
      loading(true)
      const params = { limit: 20, ordering: '-id' }
      if (search) params.protocol_number = search
      this.$store.dispatch('confirmedScientificTopicProtocol/list', params).finally(() => {
        loading(false)
      })
    },
    ensureProtocolModule() {
      if (!this.$store.hasModule('confirmedScientificTopicProtocol')) {
        this.$store.registerModule('confirmedScientificTopicProtocol', confirmedScientificTopicProtocolModule)
      }
    },
    getDcType(id) {
      return this.$store.getters['staticModule/getDcType'](id) || { name: {} }
    },
    getApplyDocumentStatus(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatus'](id) || { name: {} }
    },
    getApplyDocumentStatusColor(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatusColor'](id)
    },
    selectApplyDoc(applyDoc) {
      this.topic = {
        ...this.topic,
        apply_doc: applyDoc.id,
        apply_doc_name: applyDoc.user?.full_name || '',
      }
      this.selectedApplyDocName = applyDoc.user?.full_name || ''
      this.$bvModal.hide('confirmed-scientific-topic-students-modal')
    },
    openApprovedTopicsSearch(lang) {
      const searchValue = this.topic[`name_${lang}`]

      if (!searchValue) return

      this.approvedTopicsSearchLang = lang
      this.approvedTopicsSearchValue = searchValue
      this.$bvModal.show('confirmed-scientific-topic-approved-topics-modal')
    },
    refreshApprovedTopicsSearch() {
      if (!this.$refs.approvedTopicsSearchTable) return

      this.$refs.approvedTopicsSearchTable.searchQuery = this.approvedTopicsSearchValue

      if (this.$refs.approvedTopicsSearchTable.currentPage !== 1) {
        this.$refs.approvedTopicsSearchTable.currentPage = 1
      } else {
        this.$refs.approvedTopicsSearchTable.refetchData()
      }
    },
    valueOrDash(value) {
      return value || '-'
    },
    clearApplyDoc() {
      this.topic = {
        ...this.topic,
        apply_doc: null,
        apply_doc_name: '',
      }
      this.selectedApplyDocName = ''
    },
    clear(hide) {
      this.$store.commit('confirmedScientificTopic/FRESH')
      this.selectedApplyDocName = ''
      this.$refs.topicForm.reset()
      hide()
    },
    submit(hide) {
      this.$refs.topicForm.validate().then(success => {
        if (success) {
          this.isSubmitting = true
          this.$createSwal(this, this.topic, `${this.MODULE_NAME}/create`, () => {
            this.$emit('refetchData')
            this.$store.commit('confirmedScientificTopic/FRESH')
            this.selectedApplyDocName = ''
            this.isSubmitting = false
            hide()
          }, () => {
            this.isSubmitting = false
          })
        }
      })
    },
  },
  setup() {
    const MODULE_NAME = 'confirmedScientificTopic'
    const ORG_STUDENTS_MODULE_NAME = 'confirmedScientificTopicOrgStudents'
    const APPROVED_TOPICS_MODULE_NAME = 'confirmedScientificTopicApprovedTopics'
    const PROTOCOL_MODULE_NAME = 'confirmedScientificTopicProtocol'
    const hasTopicModule = store.hasModule(MODULE_NAME)
    const hasProtocolModule = store.hasModule(PROTOCOL_MODULE_NAME)

    if (!hasTopicModule) store.registerModule(MODULE_NAME, confirmedScientificTopicModule)
    if (!hasProtocolModule) store.registerModule(PROTOCOL_MODULE_NAME, confirmedScientificTopicProtocolModule)

    onUnmounted(() => {
      if (!hasTopicModule && store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
      if (!hasProtocolModule && store.hasModule(PROTOCOL_MODULE_NAME)) store.unregisterModule(PROTOCOL_MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ORG_STUDENTS_MODULE_NAME,
      APPROVED_TOPICS_MODULE_NAME,
    }
  },
}
</script>
